import { ActiveTemplate, CanvasElement, ColumnDefinition, TableRow } from '../types';

// ─── Constants ───────────────────────────────────────────────────────────────
const W = 302; // 80mm thermal = 302px @ 96dpi

let _eid = 0;
const eid = (prefix: string) => `th-${prefix}-${++_eid}`;

// ─── Shared helpers ───────────────────────────────────────────────────────────

function t(
  id: string, x: number, y: number, w: number, h: number,
  text: string, opts: Partial<CanvasElement> = {}
): CanvasElement {
  return {
    id, type: 'text',
    x, y, width: w, height: h,
    opacity: 100, zIndex: 5, rotation: 0,
    fontSize: 9, fontFamily: 'JetBrains Mono',
    color: '#111827', textContent: text,
    lineHeight: 1.4,
    ...opts,
  };
}
// Full-width thick bar (for accent strips)
function thickBar(id: string, y: number, h: number, color: string): CanvasElement {
  return {
    id, type: 'shape',
    x: 0, y, width: W, height: h,
    opacity: 100, zIndex: 1, rotation: 0,
    backgroundColor: color,
    borderWidth: 0, borderRadius: 0,
  };
}

// Centered rectangular badge / pill
function badge(id: string, y: number, w: number, h: number, color: string, bg: string): CanvasElement {
  return {
    id, type: 'shape',
    x: (W - w) / 2, y, width: w, height: h,
    opacity: 100, zIndex: 4, rotation: 0,
    backgroundColor: bg,
    borderColor: color,
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 4,
  };
}

// Left-aligned colored side bar (layout accent)
export function sideBar(id: string, y: number, h: number, color: string): CanvasElement {
  return {
    id, type: 'shape',
    x: 0, y, width: 4, height: h,
    opacity: 100, zIndex: 2, rotation: 0,
    backgroundColor: color,
    borderWidth: 0, borderRadius: 0,
  };
}

// Optional logo placeholder (image element — user can replace src)
function logoPlaceholder(id: string, y: number, size: number): CanvasElement {
  return {
    id,
    type: 'image',
    x: (W - size) / 2,
    y,
    width: size,
    height: size,
    opacity: 100,
    zIndex: 6,
    rotation: 0,
    // Empty src — user replaces with actual logo URL or base64
    src: '',
    // Placeholder label so it's visible in editor even when empty
    alt: 'LOGO',
  } as any;
}
function divider(id: string, y: number, dashed = false, color = '#d1d5db'): CanvasElement {
  return {
    id, type: 'shape',
    x: 8, y, width: W - 16, height: 1,
    opacity: 100, zIndex: 3, rotation: 0,
    backgroundColor: dashed ? 'transparent' : color,
    borderColor: color,
    borderWidth: dashed ? 1 : 0,
    borderStyle: dashed ? 'dashed' : 'solid',
    borderRadius: 0,
  };
}

function thermalCols(color: string): ColumnDefinition[] {
  return [
    { id: 'tc1', header: 'Item',  width: 50, align: 'left',   key: 'description' },
    { id: 'tc2', header: 'Qty',   width: 12, align: 'center', key: 'qty'         },
    { id: 'tc3', header: 'Rate',  width: 19, align: 'right',  key: 'rate'        },
    { id: 'tc4', header: 'Amt',   width: 19, align: 'right',  key: 'total'       },
  ];
}

function thermalTable(
  id: string, y: number, color: string,
  rows: TableRow[], taxRate: number, rowCount: number
): CanvasElement {
  const height = 18 + rowCount * 20 + 80;
  return {
    id, type: 'table',
    x: 8, y, width: W - 16, height,
    opacity: 100, zIndex: 10, rotation: 0,
    color, backgroundColor: '#ffffff',
    fontSize: 8, fontFamily: 'JetBrains Mono',
    columns: thermalCols(color),
    rows, taxRate, discountRate: 0,
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// 1. GROCERY SUPERMARKET
// ══════════════════════════════════════════════════════════════════════════════
export function generateGroceryThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#16a34a';
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Organic Apples 1kg',    qty: 2, rate: 89  } },
    { id: 'r2', values: { description: 'Amul Milk 500ml',       qty: 2, rate: 32  } },
    { id: 'r3', values: { description: 'Aashirvaad Atta 5kg',   qty: 1, rate: 249 } },
    { id: 'r4', values: { description: 'Fortune Sunflower Oil', qty: 1, rate: 180 } },
    { id: 'r5', values: { description: "Lay's Chips 26g",       qty: 3, rate: 20  } },
    { id: 'r6', values: { description: 'Nescafe Classic 50g',   qty: 1, rate: 145 } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Top accent bar
  el.push({ id: eid('bar'), type: 'shape', x: 0, y, width: W, height: 6, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 14;

  // Store name
  el.push(t(eid('name'), 6, y, W - 12, 16, 'FRESH MART SUPERSTORE', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Address
  el.push(t(eid('addr'), 6, y, W - 12, 22, 'Shop No.4, Main Market, Bengaluru | GSTIN: 29AABCU9603R1ZM', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 26;

  el.push(divider(eid('d1'), y));
  y += 10;

  // Bill title
  el.push(t(eid('ttl'), 6, y, W - 12, 14, 'CASH MEMO / GST BILL', { fontSize: 8, fontWeight: 'bold', textAlign: 'center' }));
  y += 18;

  // Meta info
  el.push(t(eid('meta'), 6, y, W - 12, 24, `Bill: {invoice_number}   Date: {date}\nCashier: {staff_name}  Counter: 3`, { fontSize: 7.5, color: '#374151', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d2'), y));
  y += 8;

  // Table
  const tbl = thermalTable(eid('tbl'), y, color, rows, 5, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d3'), y, true));
  y += 10;

 el.push(t(eid('thx'), 6, y, W - 12, 28, 'Thank you for shopping with us!\nfreshmart.in  •  Visit again', { fontSize: 7.5, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 34;

  // Bill number
  el.push(t(eid('recno'), 6, y, W - 12, 16, `Bill No: {invoice_number}`, { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 22;

  // Footer
  el.push(t(eid('ftr'), 6, y, W - 12, 12, 'Goods once sold will not be taken back', { fontSize: 6.5, textAlign: 'center', color: '#9ca3af' }));
  y += 24;

  return {
    name: 'Grocery Supermarket — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'Retail & Commerce',
    receiptType: 'GRC',
  } as any;
}
// ══════════════════════════════════════════════════════════════════════════════
// 2. CLINIC / PHARMACY
// ══════════════════════════════════════════════════════════════════════════════
export function generateClinicThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#0284c7';
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Consultation Fee',    qty: 1, rate: 300 } },
    { id: 'r2', values: { description: 'Crocin 500mg Strip',  qty: 2, rate: 28  } },
    { id: 'r3', values: { description: 'Augmentin 625 Strip', qty: 1, rate: 145 } },
    { id: 'r4', values: { description: 'ORS Sachet x10',      qty: 1, rate: 60  } },
    { id: 'r5', values: { description: 'BP Check (Vitals)',   qty: 1, rate: 50  } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Top accent bar
  el.push({ id: eid('bar'), type: 'shape', x: 0, y, width: W, height: 6, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 14;

  // Clinic name
  el.push(t(eid('name'), 6, y, W - 12, 16, 'DR. MEHTA HEALTH CLINIC', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Doctor name
  el.push(t(eid('dr'), 6, y, W - 12, 14, 'Dr. Rajesh Mehta  MBBS, MD (Gen. Med.)', { fontSize: 7.5, textAlign: 'center', color: '#374151' }));
  y += 18;

  // Address
  el.push(t(eid('addr'), 6, y, W - 12, 24, '12, Indira Nagar, Lucknow — 226016\nMob: 9876543210 | Reg: MCI-2345678', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d1'), y));
  y += 10;

  // Bill title
  el.push(t(eid('ttl'), 6, y, W - 12, 14, 'PATIENT RECEIPT / PHARMACY BILL', { fontSize: 8, fontWeight: 'bold', textAlign: 'center' }));
  y += 18;

  // Meta info
  const timeStr = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  el.push(t(eid('meta'), 6, y, W - 12, 36, `Receipt: {invoice_number}\nDate: {date}  Time: ${timeStr}\nDoctor: {staff_name}   Patient: [Name]`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d2'), y));
  y += 8;

  // Table
  const tbl = thermalTable(eid('tbl'), y, color, rows, 0, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d3'), y, true));
  y += 10;

  // Rx note
  el.push(t(eid('rx'), 6, y, W - 12, 14, 'Rx — Take medicines as prescribed', { fontSize: 7.5, textAlign: 'center', color: '#374151' }));
  y += 18;

  // Follow-up
  el.push(t(eid('fup'), 6, y, W - 12, 14, 'Follow-up: 7 days  |  Fast if required', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 18;





  el.push(divider(eid('d4'), y));
  y += 12;

  // QR code with bundled label
  const qrSize = 64;
  el.push({
    id: eid('qr'),
    type: 'qr',
    x: (W - qrSize) / 2,
    y,
    width: qrSize,
    height: qrSize,
    opacity: 100,
    zIndex: 10,
    rotation: 0,
    qrValue: 'https://drmehta.clinic/records',
    showLabel: true,
    labelPosition: 'bottom',
    label: 'Scan for digital prescription',
  });
  y += qrSize + 20;

  // Footer
  el.push(t(eid('ftr'), 6, y, W - 12, 12, 'Get well soon  •  Your health is our priority', { fontSize: 6.5, textAlign: 'center', color: '#9ca3af' }));
  y += 20;

  return {
    name: 'Clinic & Pharmacy — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'Medical & Wellness',
    receiptType: 'CLN',
  } as any;
}
// ══════════════════════════════════════════════════════════════════════════════
// 3. RESTAURANT / FOOD
// ══════════════════════════════════════════════════════════════════════════════
export function generateRestaurantThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#b91c1c';
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Butter Chicken',  qty: 1, rate: 280 } },
    { id: 'r2', values: { description: 'Garlic Naan',     qty: 2, rate: 40  } },
    { id: 'r3', values: { description: 'Dal Makhani',     qty: 1, rate: 180 } },
    { id: 'r4', values: { description: 'Jeera Rice',      qty: 1, rate: 120 } },
    { id: 'r5', values: { description: 'Masala Chai',     qty: 2, rate: 40  } },
    { id: 'r6', values: { description: 'Gulab Jamun 2pc', qty: 1, rate: 80  } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Top accent bar
  el.push({ id: eid('bar'), type: 'shape', x: 0, y, width: W, height: 6, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 14;

  // Restaurant name
  el.push(t(eid('name'), 6, y, W - 12, 16, 'SPICE ROUTE RESTAURANT', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Tagline
  el.push(t(eid('tag'), 6, y, W - 12, 14, 'Authentic Indian Cuisine', { fontSize: 7.5, textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }));
  y += 18;

  // Address & FSSAI
  el.push(t(eid('addr'), 6, y, W - 12, 24, 'MG Road, Pune 411001 | +91 9900112233\nFSSAI: 11223344556677 | GST: 27AABCU9603', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d1'), y));
  y += 10;

  // Table info
  el.push(t(eid('tblno'), 6, y, W - 12, 14, 'TABLE: 07  |  COVERS: 2  |  KOT: #441', { fontSize: 8, fontWeight: 'bold', textAlign: 'center' }));
  y += 18;

  // Bill meta
  const timeStr = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  el.push(t(eid('meta'), 6, y, W - 12, 24, `Bill: {invoice_number}   Date: {date}\nWaiter: {staff_name}  Time: ${timeStr}`, { fontSize: 7.5, color: '#374151', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d2'), y));
  y += 8;

  // Items table
  const tblEl = thermalTable(eid('items'), y, color, rows, 5, rows.length);
  el.push(tblEl);
  y += (tblEl.height as number) + 10;

  el.push(divider(eid('d3'), y, true));
  y += 10;

  // Tax note
  el.push(t(eid('tax'), 6, y, W - 12, 24, 'SGST 2.5% + CGST 2.5% incl. in total\nService Charge 5% incl. in total', { fontSize: 7, color: '#6b7280', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d4'), y));
  y += 10;

  // Thank you
  el.push(t(eid('thx'), 6, y, W - 12, 16, 'Thank you for dining with us! 🙏', { fontSize: 9, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Review prompt
  el.push(t(eid('rev'), 6, y, W - 12, 12, 'Rate us on Zomato / Google Maps', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 16;

  // QR code with bundled label
  const qrSize = 64;
  el.push({
    id: eid('qr'),
    type: 'qr',
    x: (W - qrSize) / 2,
    y,
    width: qrSize,
    height: qrSize,
    opacity: 100,
    zIndex: 10,
    rotation: 0,
    qrValue: 'https://g.page/spiceroute/review',
    showLabel: true,
    labelPosition: 'bottom',
    label: 'Scan to review',
  });
  y += qrSize + 20;

  // Footer
  el.push(t(eid('ftr'), 6, y, W - 12, 12, 'spiceroute.in', { fontSize: 6.5, textAlign: 'center', color: '#9ca3af' }));
  y += 20;

  return {
    name: 'Restaurant & Food — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'Food & Hospitality',
    receiptType: 'RST',
  } as any;
}

// ══════════════════════════════════════════════════════════════════════════════
// 4. RETAIL SHOP
// ══════════════════════════════════════════════════════════════════════════════
export function generateRetailThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#7c3aed';
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Cotton T-Shirt L',    qty: 2, rate: 399  } },
    { id: 'r2', values: { description: 'Casual Chinos 32×30', qty: 1, rate: 899  } },
    { id: 'r3', values: { description: 'Canvas Sneakers Sz9', qty: 1, rate: 749  } },
    { id: 'r4', values: { description: 'Leather Wallet',      qty: 1, rate: 349  } },
    { id: 'r5', values: { description: 'Loyalty Disc 10%',    qty: 1, rate: -239 } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Top accent bar
  el.push({ id: eid('bar'), type: 'shape', x: 0, y, width: W, height: 6, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 14;

  // Store name
  el.push(t(eid('name'), 6, y, W - 12, 16, 'TRENDZ RETAIL STORE', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Address
  el.push(t(eid('addr'), 6, y, W - 12, 28, 'Forum Mall, 2F, Koramangala, Bengaluru 560034\n080-4567-8901 | GSTIN: 29AABCU9603R1ZM', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 32;

  el.push(divider(eid('d1'), y));
  y += 10;

  // Invoice title
  el.push(t(eid('ttl'), 6, y, W - 12, 14, 'TAX INVOICE', { fontSize: 9, fontWeight: 'bold', textAlign: 'center' }));
  y += 18;

  // Meta info
  const timeStr = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  el.push(t(eid('meta'), 6, y, W - 12, 36, `Invoice: {invoice_number}\nDate: {date}  Time: ${timeStr}\nCustomer: Walk-in  Staff: {staff_name}`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d2'), y));
  y += 8;

  // Items table
  const tblEl = thermalTable(eid('items'), y, color, rows, 12, rows.length);
  el.push(tblEl);
  y += (tblEl.height as number) + 10;

  el.push(divider(eid('d3'), y, true));
  y += 10;

  // Exchange policy
  el.push(t(eid('pol'), 6, y, W - 12, 24, 'Exchange within 7 days with bill.\nNo returns on sale items.', { fontSize: 7.5, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d4'), y));
  y += 10;

  // Invoice number
  el.push(t(eid('recno'), 6, y, W - 12, 16, `Invoice: {invoice_number}`, { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Thank you
  el.push(t(eid('thx'), 6, y, W - 12, 14, 'Thank you! Visit us again.', { fontSize: 9, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  // Footer
  el.push(t(eid('ftr'), 6, y, W - 12, 12, 'trendz.in  •  @trendz_store', { fontSize: 6.5, textAlign: 'center', color: '#9ca3af' }));
  y += 20;

  return {
    name: 'Retail Shop — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'Retail & Commerce',
    receiptType: 'RTL',
  } as any;
}

// ══════════════════════════════════════════════════════════════════════════════
// 5. CAFE / COFFEE SHOP
// ══════════════════════════════════════════════════════════════════════════════
export function generateCafeThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#7c2d12';
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Flat White',        qty: 2, rate: 180 } },
    { id: 'r2', values: { description: 'Croissant',         qty: 2, rate: 120 } },
    { id: 'r3', values: { description: 'Cold Brew 350ml',   qty: 1, rate: 220 } },
    { id: 'r4', values: { description: 'Blueberry Muffin',  qty: 1, rate: 90  } },
    { id: 'r5', values: { description: 'Matcha Latte',      qty: 1, rate: 210 } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Top accent bar
  el.push({ id: eid('bar'), type: 'shape', x: 0, y, width: W, height: 6, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 14;

  // Cafe name
  el.push(t(eid('name'), 6, y, W - 12, 16, 'BREW & BLOOM CAFE', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Tagline
  el.push(t(eid('tag'), 6, y, W - 12, 14, 'Specialty Coffee & Artisan Bakes', { fontSize: 7.5, textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }));
  y += 18;

  // Address
  el.push(t(eid('addr'), 6, y, W - 12, 24, 'Church Street, Bengaluru 560001\nWifi: BrewBloom_Guest | +91 9845012345', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d1'), y));
  y += 10;

  // Order meta
  const timeStr = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  el.push(t(eid('meta'), 6, y, W - 12, 24, `Order: {invoice_number}   {date}  ${timeStr}\nBarista: {staff_name}  Table: 5`, { fontSize: 7.5, color: '#374151', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d2'), y));
  y += 8;

  // Items table
  const tblEl = thermalTable(eid('items'), y, color, rows, 5, rows.length);
  el.push(tblEl);
  y += (tblEl.height as number) + 10;

  el.push(divider(eid('d3'), y, true));
  y += 10;

  // Thank you + social
  el.push(t(eid('thx'), 6, y, W - 12, 24, 'Thanks for the vibe ☕\nbrewandbloom.in  •  @brewbloom', { fontSize: 7.5, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 28;

  // QR code with bundled label
  const qrSize = 60;
  el.push({
    id: eid('qr'),
    type: 'qr',
    x: (W - qrSize) / 2,
    y,
    width: qrSize,
    height: qrSize,
    opacity: 100,
    zIndex: 10,
    rotation: 0,
    qrValue: 'https://brewandbloom.in/menu',
    showLabel: true,
    labelPosition: 'bottom',
    label: 'Scan to view our full menu',
  });
  y += qrSize + 20;

  return {
    name: 'Cafe & Coffee Shop — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'Food & Hospitality',
    receiptType: 'CAF',
  } as any;
}

// ══════════════════════════════════════════════════════════════════════════════
// 6. PHARMACY / MEDICAL STORE
// ══════════════════════════════════════════════════════════════════════════════
export function generatePharmacyThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#059669';
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Dolo 650mg (15 tabs)', qty: 2, rate: 32  } },
    { id: 'r2', values: { description: 'Azithral 500 Strip',   qty: 1, rate: 98  } },
    { id: 'r3', values: { description: 'Combiflam Tabs x10',   qty: 1, rate: 45  } },
    { id: 'r4', values: { description: 'Volini Gel 50g',       qty: 1, rate: 135 } },
    { id: 'r5', values: { description: 'Vitamin C 500mg x30',  qty: 1, rate: 180 } },
    { id: 'r6', values: { description: 'Surgical Mask x50',    qty: 1, rate: 120 } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Top accent bar
  el.push({ id: eid('bar'), type: 'shape', x: 0, y, width: W, height: 6, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 14;

  // Pharmacy name
  el.push(t(eid('cross'), 6, y, W - 12, 16, '✚  APOLLO PHARMACY  ✚', { fontSize: 11, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Address & license
  el.push(t(eid('addr'), 6, y, W - 12, 24, 'HSR Layout, Bengaluru 560102\nDL: KA-BLR-1234  |  GSTIN: 29AAACA1234A1Z5', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d1'), y));
  y += 10;

  // Bill title
  el.push(t(eid('ttl'), 6, y, W - 12, 14, 'MEDICINE SALE BILL', { fontSize: 8, fontWeight: 'bold', textAlign: 'center' }));
  y += 18;

  // Meta info
  const timeStr = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  el.push(t(eid('meta'), 6, y, W - 12, 24, `Bill: {invoice_number}   {date}  ${timeStr}\nPharmacist: {staff_name}  Reg: PH-4521`, { fontSize: 7.5, color: '#374151', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d2'), y));
  y += 8;

  // Items table
  const tblEl = thermalTable(eid('items'), y, color, rows, 0, rows.length);
  el.push(tblEl);
  y += (tblEl.height as number) + 10;

  el.push(divider(eid('d3'), y, true));
  y += 10;

  // Warning
  el.push(t(eid('warn'), 6, y, W - 12, 24, 'Take medicines as directed by doctor.\nKeep out of reach of children.', { fontSize: 7, textAlign: 'center', color: '#374151', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d4'), y));
  y += 10;

  // Bill number
  el.push(t(eid('recno'), 6, y, W - 12, 16, `Bill No: {invoice_number}`, { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Footer
  el.push(t(eid('ftr'), 6, y, W - 12, 12, 'Your health is our commitment', { fontSize: 6.5, textAlign: 'center', color: '#9ca3af' }));
  y += 20;

  return {
    name: 'Pharmacy / Medical Store — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'Medical & Wellness',
    receiptType: 'PHR',
  } as any;
}
// ══════════════════════════════════════════════════════════════════════════════
// 7. FAST FOOD / QSR
// ══════════════════════════════════════════════════════════════════════════════
export function generateFastFoodThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#ea580c';
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Veg Burger Combo',   qty: 2, rate: 199 } },
    { id: 'r2', values: { description: 'Chicken Wings x6',   qty: 1, rate: 249 } },
    { id: 'r3', values: { description: 'Large Fries',        qty: 2, rate: 89  } },
    { id: 'r4', values: { description: 'Pepsi 500ml',        qty: 3, rate: 60  } },
    { id: 'r5', values: { description: 'Soft Serve Cup',     qty: 2, rate: 49  } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Top accent bar
  el.push({ id: eid('bar'), type: 'shape', x: 0, y, width: W, height: 6, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 14;

  // Restaurant name
  el.push(t(eid('name'), 6, y, W - 12, 16, 'BURGER BARN', { fontSize: 13, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Tagline
  el.push(t(eid('tag'), 6, y, W - 12, 14, 'Fast Food  •  Quick Service', { fontSize: 7.5, textAlign: 'center', color: '#6b7280' }));
  y += 18;

  // Address
  el.push(t(eid('addr'), 6, y, W - 12, 14, 'Phoenix Mall, Whitefield, Bengaluru | FSSAI: 10020042012345', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 18;

  el.push(divider(eid('d1'), y));
  y += 10;

  // Order meta
  const timeStr = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  el.push(t(eid('meta'), 6, y, W - 12, 24, `Order: {invoice_number}  DINE-IN  Token: #47\nDate: {date}  ${timeStr}  Cashier: {staff_name}`, { fontSize: 7.5, color: '#374151', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d2'), y));
  y += 8;

  // Items table
  const tblEl = thermalTable(eid('items'), y, color, rows, 5, rows.length);
  el.push(tblEl);
  y += (tblEl.height as number) + 10;

  el.push(divider(eid('d3'), y, true));
  y += 10;

  // Thank you
  el.push(t(eid('thx'), 6, y, W - 12, 16, 'Enjoy your meal! 🍔', { fontSize: 9, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Review prompt
  el.push(t(eid('rev'), 6, y, W - 12, 12, 'Rate us on Swiggy / Zomato', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 16;

  // Order number
  el.push(t(eid('recno'), 6, y, W - 12, 16, `Order No: {invoice_number}`, { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Footer
  el.push(t(eid('ftr'), 6, y, W - 12, 12, 'burgerbarn.in  •  Takeaway welcome', { fontSize: 6.5, textAlign: 'center', color: '#9ca3af' }));
  y += 20;

  return {
    name: 'Fast Food / QSR — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'Food & Hospitality',
    receiptType: 'QSR',
  } as any;
}
// ══════════════════════════════════════════════════════════════════════════════
// 8. SALON / BEAUTY PARLOUR
// ══════════════════════════════════════════════════════════════════════════════
export function generateSalonThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#db2777';
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Haircut & Styling',    qty: 1, rate: 450  } },
    { id: 'r2', values: { description: 'Keratin Treatment',    qty: 1, rate: 1800 } },
    { id: 'r3', values: { description: 'Eyebrow Threading',    qty: 1, rate: 80   } },
    { id: 'r4', values: { description: 'Face Clean-up',        qty: 1, rate: 350  } },
    { id: 'r5', values: { description: 'Hair Spa (60 min)',    qty: 1, rate: 799  } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Top accent bar
  el.push({ id: eid('bar'), type: 'shape', x: 0, y, width: W, height: 6, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 14;

  // Salon name
  el.push(t(eid('name'), 6, y, W - 12, 16, 'GLAM STUDIO SALON', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Tagline
  el.push(t(eid('tag'), 6, y, W - 12, 14, 'Hair  •  Skin  •  Beauty', { fontSize: 8, textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }));
  y += 18;

  // Address
  el.push(t(eid('addr'), 6, y, W - 12, 24, 'Jayanagar 4th Block, Bengaluru 560041\n+91 9611223344  |  glamstudio.in', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d1'), y));
  y += 10;

  // Appointment meta
  const timeStr = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  el.push(t(eid('meta'), 6, y, W - 12, 24, `Appt: {invoice_number}   {date}  ${timeStr}\nStylist: {staff_name}  Client: Walk-in`, { fontSize: 7.5, color: '#374151', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d2'), y));
  y += 8;

  // Items table
  const tblEl = thermalTable(eid('items'), y, color, rows, 18, rows.length);
  el.push(tblEl);
  y += (tblEl.height as number) + 10;

  el.push(divider(eid('d3'), y, true));
  y += 10;

  // Thank you
  el.push(t(eid('thx'), 6, y, W - 12, 24, 'You look amazing! 💄\nBook next visit: glamstudio.in', { fontSize: 7.5, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 28;

  // QR code with bundled label
  const qrSize = 60;
  el.push({
    id: eid('qr'),
    type: 'qr',
    x: (W - qrSize) / 2,
    y,
    width: qrSize,
    height: qrSize,
    opacity: 100,
    zIndex: 10,
    rotation: 0,
    qrValue: 'https://glamstudio.in/book',
    showLabel: true,
    labelPosition: 'bottom',
    label: 'Scan to book your next appointment',
  });
  y += qrSize + 20;

  return {
    name: 'Salon & Beauty Parlour — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'Retail & Commerce',
    receiptType: 'SLN',
  } as any;
}

// ══════════════════════════════════════════════════════════════════════════════
// 9. PETROL PUMP / FUEL STATION
// ══════════════════════════════════════════════════════════════════════════════
export function generateFuelStationThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#0369a1';
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Petrol (Litres)',    qty: 12, rate: 103.44 } },
    { id: 'r2', values: { description: 'Engine Oil 1L',      qty: 1,  rate: 450   } },
    { id: 'r3', values: { description: 'Coolant Top-up',     qty: 1,  rate: 120   } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Top accent bar
  el.push({ id: eid('bar'), type: 'shape', x: 0, y, width: W, height: 6, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 14;

  // Station name
  el.push(t(eid('name'), 6, y, W - 12, 16, 'INDIAN OIL — SPEED PUMPS', { fontSize: 11, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Address
  el.push(t(eid('addr'), 6, y, W - 12, 24, 'NH-48, Tumkur Road, Bengaluru 560073\nDealer: Rajan Enterprises | SAC: 99820', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d1'), y));
  y += 10;

  // Receipt title
  el.push(t(eid('ttl'), 6, y, W - 12, 14, 'FUEL DISPENSING RECEIPT', { fontSize: 8, fontWeight: 'bold', textAlign: 'center' }));
  y += 18;

  // Meta info
  const timeStr = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  el.push(t(eid('meta'), 6, y, W - 12, 36, `Receipt: {invoice_number}   Date: {date}\nTime: ${timeStr}   Attendant: {staff_name}\nNozzle: 03  Vehicle: KA-05-MJ-1234`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d2'), y));
  y += 8;

  // Items table
  const tblEl = thermalTable(eid('items'), y, color, rows, 0, rows.length);
  el.push(tblEl);
  y += (tblEl.height as number) + 10;

  el.push(divider(eid('d3'), y, true));
  y += 4;

  // Tax note
  el.push(t(eid('note'), 6, y, W - 12, 24, 'Fuel price includes all applicable taxes.\nPrice as per govt. revised rate.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d4'), y));
  y += 10;

  // Footer
  el.push(t(eid('ftr'), 6, y, W - 12, 12, 'Drive safe  •  iocl.com', { fontSize: 6.5, textAlign: 'center', color: '#9ca3af' }));
  y += 20;

  return {
    name: 'Petrol Pump / Fuel Station — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'Retail & Commerce',
    receiptType: 'FUL',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 10. GYM / FITNESS CENTER
// ══════════════════════════════════════════════════════════════════════════════
export function generateGymThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#18181b';
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Monthly Membership',   qty: 1, rate: 1499 } },
    { id: 'r2', values: { description: 'Personal Training x4', qty: 4, rate: 600  } },
    { id: 'r3', values: { description: 'Whey Protein 1kg',     qty: 1, rate: 2200 } },
    { id: 'r4', values: { description: 'Locker Rental/Month',  qty: 1, rate: 200  } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Top accent bar
  el.push({ id: eid('bar'), type: 'shape', x: 0, y, width: W, height: 6, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 14;

  // Gym name
  el.push(t(eid('name'), 6, y, W - 12, 16, '⚡ IRON TEMPLE GYM', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Tagline
  el.push(t(eid('tag'), 6, y, W - 12, 14, 'Strength  •  Cardio  •  Nutrition', { fontSize: 7.5, textAlign: 'center', color: '#6b7280' }));
  y += 18;

  // Address
  el.push(t(eid('addr'), 6, y, W - 12, 24, 'Indiranagar 100ft Road, Bengaluru 560038\n+91 9900887766  |  irontemple.fit', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d1'), y));
  y += 10;

  // Receipt meta
  el.push(t(eid('meta'), 6, y, W - 12, 24, `Receipt: {invoice_number}   Date: {date}\nStaff: {staff_name}   ID: MEM-2341`, { fontSize: 7.5, color: '#374151', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d2'), y));
  y += 8;

  // Items table
  const tblEl = thermalTable(eid('items'), y, color, rows, 18, rows.length);
  el.push(tblEl);
  y += (tblEl.height as number) + 10;

  el.push(divider(eid('d3'), y, true));
  y += 10;

  // Validity note
  el.push(t(eid('valid'), 6, y, W - 12, 24, 'Membership valid for 30 days from date.\nNon-transferable. No refunds.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d4'), y));
  y += 10;

  // Motivational line
  el.push(t(eid('thx'), 6, y, W - 12, 16, 'GRIND. GROW. REPEAT. 💪', { fontSize: 9, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Member number
  el.push(t(eid('recno'), 6, y, W - 12, 16, `Member No: {invoice_number}`, { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Footer
  el.push(t(eid('ftr'), 6, y, W - 12, 12, 'irontemple.fit  •  Open 5am–11pm', { fontSize: 6.5, textAlign: 'center', color: '#9ca3af' }));
  y += 20;

  return {
    name: 'Gym & Fitness Center — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'Medical & Wellness',
    receiptType: 'GYM',
  } as any;
}

// ══════════════════════════════════════════════════════════════════════════════
// 11. HOTEL / LODGE  — Layout C (inverted dark header)
// ══════════════════════════════════════════════════════════════════════════════
export function generateHotelThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#92400e';       // warm amber-brown
  const headerBg = '#1c1917';    // near-black
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Deluxe Room (2N)',    qty: 2,  rate: 2200 } },
    { id: 'r2', values: { description: 'Breakfast Buffet',    qty: 4,  rate: 250  } },
    { id: 'r3', values: { description: 'Room Service',        qty: 1,  rate: 450  } },
    { id: 'r4', values: { description: 'Airport Transfer',    qty: 1,  rate: 800  } },
    { id: 'r5', values: { description: 'Laundry',             qty: 1,  rate: 180  } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Dark header block
  el.push({ id: eid('hdr'), type: 'shape', x: 0, y, width: W, height: 80, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: headerBg, borderWidth: 0, borderRadius: 0 });

  // Hotel name (white on dark)
  el.push(t(eid('name'), 6, y + 8, W - 12, 16, 'THE GRAND HAVEN', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color: '#fef3c7' }));
  // Tagline
  el.push(t(eid('tag'), 6, y + 26, W - 12, 12, 'Where comfort meets elegance', { fontSize: 7, textAlign: 'center', color: '#d6d3d1', fontStyle: 'italic' }));
  // Address
  el.push(t(eid('addr'), 6, y + 40, W - 12, 24, 'Race Course Rd, Mysuru 570001 | +91 8210443300', { fontSize: 7, textAlign: 'center', color: '#a8a29e' }));
  y += 88;

  // Amber accent line
  el.push({ id: eid('acl'), type: 'shape', x: 0, y, width: W, height: 3, opacity: 100, zIndex: 2, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 10;

  // Bill title
  el.push(t(eid('ttl'), 6, y, W - 12, 14, 'GUEST FOLIO / HOTEL BILL', { fontSize: 8, fontWeight: 'bold', textAlign: 'center' }));
  y += 18;
  el.push(t(eid('meta'), 6, y, W - 12, 36, `Folio: {invoice_number}   Check-in: {date}\nReception: {staff_name}   Room: 204\nGuest: [Name]`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d1'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 12, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d2'), y, true));
  y += 10;

  el.push(t(eid('pay'), 6, y, W - 12, 14, 'Payment: UPI / Card / Cash', { fontSize: 7.5, textAlign: 'center', color: '#374151' }));
  y += 18;

  el.push(t(eid('thx'), 6, y, W - 12, 14, 'Thank you for staying with us!', { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  // Bottom amber strip
  el.push({ id: eid('foot'), type: 'shape', x: 0, y, width: W, height: 4, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 12;

  return {
    name: 'Hotel & Lodge — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'HTL',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 12. ELECTRONICS STORE  — Layout A (top + bottom bars, logo slot)
// ══════════════════════════════════════════════════════════════════════════════
export function generateElectronicsThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#1d4ed8'; // electric blue
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Samsung 65" QLED',     qty: 1, rate: 74999 } },
    { id: 'r2', values: { description: 'HDMI 2.1 Cable 2m',    qty: 2, rate: 799   } },
    { id: 'r3', values: { description: 'Wall Mount Bracket',   qty: 1, rate: 1499  } },
    { id: 'r4', values: { description: 'Extended Warranty 2Y', qty: 1, rate: 2999  } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Top bar
  el.push(thickBar(eid('top'), y, 5, color));
  y += 12;

  // Logo placeholder
  el.push(logoPlaceholder(eid('logo'), y, 32));
  y += 40;

  el.push(t(eid('name'), 6, y, W - 12, 16, 'NEXUS ELECTRONICS', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('addr'), 6, y, W - 12, 22, 'Brigade Road, Bengaluru 560025 | GSTIN: 29AABCU9603R1ZM\n+91 8023344556', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(divider(eid('d1'), y));
  y += 10;

  el.push(t(eid('ttl'), 6, y, W - 12, 14, 'TAX INVOICE (GST)', { fontSize: 9, fontWeight: 'bold', textAlign: 'center' }));
  y += 18;
  el.push(t(eid('meta'), 6, y, W - 12, 36, `Invoice: {invoice_number}   Date: {date}\nSales Exec: {staff_name}   POS: Counter-1\nCustomer: Walk-in`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d2'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 18, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d3'), y, true));
  y += 10;

  el.push(t(eid('wrnt'), 6, y, W - 12, 22, 'Warranty claims: service.nexus.in\nKeep this bill for warranty purposes.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('inv'), 6, y, W - 12, 14, `Invoice: {invoice_number}`, { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  // Bottom bar
  el.push(thickBar(eid('bot'), y, 5, color));
  y += 12;

  return {
    name: 'Electronics Store — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'ELC',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 13. HARDWARE / TOOLS SHOP  — Layout B (left sidebar accent)
// ══════════════════════════════════════════════════════════════════════════════
export function generateHardwareThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#b45309'; // rust orange
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'PVC Pipe 1" x 3m',    qty: 4,  rate: 85  } },
    { id: 'r2', values: { description: 'Drill Bit Set 13pc',   qty: 1,  rate: 349 } },
    { id: 'r3', values: { description: 'Cement Bag 50kg',      qty: 2,  rate: 380 } },
    { id: 'r4', values: { description: 'Wire 4sq mm (10m)',    qty: 1,  rate: 520 } },
    { id: 'r5', values: { description: 'Switch Board 6-way',   qty: 2,  rate: 180 } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Left sidebar (runs full height — we'll set height at the end)
  // We'll add it after calculating total height

  // Logo placeholder (top-left area, offset for sidebar)
  el.push(logoPlaceholder(eid('logo'), y + 6, 30));
  y += 44;

  el.push(t(eid('name'), 10, y, W - 18, 16, 'BUILDRIGHT HARDWARE', { fontSize: 11, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('addr'), 10, y, W - 18, 22, 'Old Town Rd, Hubli 580029 | +91 9900112233', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.5 }));
  y += 26;

  el.push(divider(eid('d1'), y));
  y += 10;

  el.push(t(eid('ttl'), 10, y, W - 18, 14, 'MATERIAL SUPPLY BILL', { fontSize: 8.5, fontWeight: 'bold', textAlign: 'center' }));
  y += 18;
  el.push(t(eid('meta'), 10, y, W - 18, 24, `Bill: {invoice_number}   Date: {date}\nSales: {staff_name}   Delivery: Yes`, { fontSize: 7.5, color: '#374151', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d2'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 18, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d3'), y, true));
  y += 10;

  el.push(t(eid('ret'), 10, y, W - 18, 22, 'Returns accepted within 3 days with bill.\nNo returns on cut/custom items.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('thx'), 10, y, W - 18, 14, 'Build strong. Build right.', { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  // Now push the left sidebar with full height
  el.push(divider(eid('d4'), y));

  return {
    name: 'Hardware & Tools Shop — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'HRD',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 14. MOBILE RECHARGE / DTH  — Layout D (badge header)
// ══════════════════════════════════════════════════════════════════════════════
export function generateRechargeThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#7c3aed'; // violet
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Jio 5G — 84 Days',   qty: 1, rate: 719 } },
    { id: 'r2', values: { description: 'Airtel DTH — 1 Mo',  qty: 1, rate: 349 } },
    { id: 'r3', values: { description: 'Tata Play — 1 Mo',   qty: 1, rate: 299 } },
    { id: 'r4', values: { description: 'Service Charge',      qty: 1, rate: 20  } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Top bar thin
  el.push(thickBar(eid('top'), y, 4, color));
  y += 12;

  // Logo placeholder
  el.push(logoPlaceholder(eid('logo'), y, 30));
  y += 38;

  el.push(t(eid('name'), 6, y, W - 12, 16, 'CONNECT POINT', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('sub'), 6, y, W - 12, 12, 'Mobile Recharge & DTH Services', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 16;
  el.push(t(eid('addr'), 6, y, W - 12, 12, 'Gandhi Bazaar, Bengaluru | +91 9876501234', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 16;

  el.push(divider(eid('d1'), y));
  y += 10;

  // Badge: "RECHARGE RECEIPT"
  el.push(badge(eid('bdg'), y, 140, 18, color, '#ede9fe'));
  el.push(t(eid('bdgtxt'), (W - 140) / 2, y + 2, 140, 14, 'RECHARGE RECEIPT', { fontSize: 7.5, fontWeight: 'bold', textAlign: 'center', color }));
  y += 26;
  const timeStr = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  el.push(t(eid('meta'), 6, y, W - 12, 24, `Txn: {invoice_number}   {date}  ${timeStr}\nOperator: {staff_name}`, { fontSize: 7.5, color: '#374151', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d2'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 0, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d3'), y, true));
  y += 4;

  el.push(t(eid('note'), 6, y, W - 12, 22, 'Recharge activated within 5 mins.\nFor issues call 1800-889-9900.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('txn'), 6, y, W - 12, 14, `Txn ID: {invoice_number}`, { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  el.push(thickBar(eid('bot'), y, 4, color));
  y += 10;

  return {
    name: 'Mobile Recharge & DTH — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'RCH',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 15. STATIONERY / BOOK SHOP  — Layout E (minimal, no top bar)
// ══════════════════════════════════════════════════════════════════════════════
export function generateStationeryThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#064e3b'; // deep teal-green
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Classmate Notebook A4', qty: 3, rate: 55  } },
    { id: 'r2', values: { description: 'Reynolds Ball Pen x10', qty: 2, rate: 65  } },
    { id: 'r3', values: { description: 'Stapler + 1000 Pins',   qty: 1, rate: 180 } },
    { id: 'r4', values: { description: 'A4 Paper Ream 500sh',   qty: 2, rate: 250 } },
    { id: 'r5', values: { description: 'Sticky Notes 3" Asst',  qty: 3, rate: 40  } },
  ];
  let y = 8;
  const el: CanvasElement[] = [];

  // Logo placeholder — top center (larger, no bar above)
  el.push(logoPlaceholder(eid('logo'), y, 40));
  y += 50;

  // Double thin rule
  el.push(divider(eid('r1'), y, false, color));
  y += 3;
  el.push(divider(eid('r2'), y, false, color));
  y += 10;

  el.push(t(eid('name'), 6, y, W - 12, 16, 'PAPER & PEN STATIONERY', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('addr'), 6, y, W - 12, 22, 'Lalbagh Road, Bengaluru 560027 | +91 9090112233\nGSTIN: 29AABCU9603R1ZM', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(divider(eid('r3'), y, false, color));
  y += 3;
  el.push(divider(eid('r4'), y, false, color));
  y += 10;

  el.push(t(eid('ttl'), 6, y, W - 12, 14, 'RETAIL BILL', { fontSize: 8.5, fontWeight: 'bold', textAlign: 'center' }));
  y += 18;
  el.push(t(eid('meta'), 6, y, W - 12, 24, `Bill: {invoice_number}   Date: {date}\nSales: {staff_name}`, { fontSize: 7.5, color: '#374151', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d1'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 12, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d2'), y, true));
  y += 10;

  el.push(t(eid('thx'), 6, y, W - 12, 14, 'Thank you! Happy Learning!', { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  el.push(divider(eid('r5'), y, false, color));
  y += 3;
  el.push(divider(eid('r6'), y, false, color));
  y += 14;

  return {
    name: 'Stationery & Book Shop — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'STN',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 16. LAUNDRY / DRY CLEANING  — Layout D (badge header)
// ══════════════════════════════════════════════════════════════════════════════
export function generateLaundryThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#0e7490'; // cyan teal
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Shirt (Wash+Press)',    qty: 5,  rate: 40  } },
    { id: 'r2', values: { description: 'Trouser (Dry Clean)',   qty: 3,  rate: 90  } },
    { id: 'r3', values: { description: 'Saree (Dry Clean)',     qty: 2,  rate: 180 } },
    { id: 'r4', values: { description: 'Blanket (Wash)',        qty: 1,  rate: 200 } },
    { id: 'r5', values: { description: 'Suit Jacket (Press)',   qty: 2,  rate: 80  } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  el.push(thickBar(eid('top'), y, 5, color));
  y += 12;

  // Logo placeholder
  el.push(logoPlaceholder(eid('logo'), y, 30));
  y += 38;

  el.push(t(eid('name'), 6, y, W - 12, 16, 'SPARKLE LAUNDRY', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('addr'), 6, y, W - 12, 22, 'BTM Layout, Bengaluru 560076\n+91 9988776655 | sparklelaundry.in', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(divider(eid('d1'), y));
  y += 10;

  // Badge: "LAUNDRY ORDER"
  el.push(badge(eid('bdg'), y, 130, 18, color, '#ecfeff'));
  el.push(t(eid('bdgtxt'), (W - 130) / 2, y + 2, 130, 14, 'LAUNDRY ORDER', { fontSize: 7.5, fontWeight: 'bold', textAlign: 'center', color }));
  y += 26;
  const pickup = 'After 48 hrs';
  el.push(t(eid('meta'), 6, y, W - 12, 36, `Order: {invoice_number}   Drop: {date}\nStaff: {staff_name}   Pickup: ${pickup}\nCustomer: [Name]`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d2'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 0, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d3'), y, true));
  y += 4;

  el.push(t(eid('note'), 6, y, W - 12, 22, 'Items not collected within 15 days will\nnot be our responsibility.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('token'), 6, y, W - 12, 16, `Token: {invoice_number}`, { fontSize: 10, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  el.push(thickBar(eid('bot'), y, 5, color));
  y += 10;

  return {
    name: 'Laundry & Dry Cleaning — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'LDR',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 17. AUTO WORKSHOP / GARAGE  — Layout C (dark header)
// ══════════════════════════════════════════════════════════════════════════════
export function generateGarageThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#dc2626'; // red
  const headerBg = '#18181b';
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Oil Change (Full Syn)', qty: 1, rate: 1200 } },
    { id: 'r2', values: { description: 'Air Filter Replace',    qty: 1, rate: 350  } },
    { id: 'r3', values: { description: 'Brake Pad (Front)',     qty: 2, rate: 850  } },
    { id: 'r4', values: { description: 'Wheel Alignment',       qty: 1, rate: 400  } },
    { id: 'r5', values: { description: 'Labour Charges',        qty: 1, rate: 600  } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Dark header block
  el.push({ id: eid('hdr'), type: 'shape', x: 0, y, width: W, height: 82, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: headerBg, borderWidth: 0, borderRadius: 0 });

  // Logo placeholder inside dark block
  el.push(logoPlaceholder(eid('logo'), y + 6, 34));

  el.push(t(eid('name'), 6, y + 8, W - 12, 16, '⚙ SPEEDFIX GARAGE', { fontSize: 11, fontWeight: 'bold', textAlign: 'center', color: '#fee2e2' }));
  el.push(t(eid('tag'), 6, y + 26, W - 12, 12, 'Multi-brand Car & Bike Service', { fontSize: 7, textAlign: 'center', color: '#a1a1aa', fontStyle: 'italic' }));
  el.push(t(eid('addr'), 6, y + 40, W - 12, 22, 'Ring Road, Mysuru 570008 | +91 9845001122\nGST: 29AABCU9603R1ZM', { fontSize: 7, textAlign: 'center', color: '#71717a', lineHeight: 1.6 }));
  y += 88;

  el.push({ id: eid('acl'), type: 'shape', x: 0, y, width: W, height: 3, opacity: 100, zIndex: 2, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 10;

  el.push(t(eid('ttl'), 6, y, W - 12, 14, 'JOB CARD / SERVICE BILL', { fontSize: 8, fontWeight: 'bold', textAlign: 'center' }));
  y += 18;
  el.push(t(eid('meta'), 6, y, W - 12, 36, `Job No: {invoice_number}   Date: {date}\nMechanic: {staff_name}   Reg: ___\nVehicle: [Make/Model]`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d1'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 18, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d2'), y, true));
  y += 10;

  el.push(t(eid('wrnt'), 6, y, W - 12, 22, 'Parts warranty: 3 months / 3000km.\nPresent bill for warranty claims.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('job'), 6, y, W - 12, 14, `Job No: {invoice_number}`, { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  el.push({ id: eid('bot'), type: 'shape', x: 0, y, width: W, height: 3, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 10;

  return {
    name: 'Auto Workshop / Garage — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'GRG',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 18. VEGETABLE / FRUIT VENDOR  — Layout B (left sidebar)
// ══════════════════════════════════════════════════════════════════════════════
export function generateVegetableThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#15803d'; // vegetable green
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Tomato 1kg',         qty: 2,  rate: 40  } },
    { id: 'r2', values: { description: 'Onion 2kg',          qty: 1,  rate: 60  } },
    { id: 'r3', values: { description: 'Capsicum 500g',      qty: 1,  rate: 35  } },
    { id: 'r4', values: { description: 'Spinach Bunch',      qty: 2,  rate: 20  } },
    { id: 'r5', values: { description: 'Carrot 1kg',         qty: 1,  rate: 50  } },
    { id: 'r6', values: { description: 'Mango Alphonso 1kg', qty: 1,  rate: 180 } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  el.push(logoPlaceholder(eid('logo'), y + 6, 30));
  y += 44;

  el.push(t(eid('name'), 10, y, W - 18, 16, '🌿 FRESH HARVEST', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('tag'), 10, y, W - 18, 12, 'Farm-to-table Fruits & Vegetables', { fontSize: 7, textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }));
  y += 16;
  el.push(t(eid('addr'), 10, y, W - 18, 12, 'City Market, Bengaluru | +91 9900554433', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 16;

  el.push(divider(eid('d1'), y));
  y += 10;
  el.push(t(eid('meta'), 10, y, W - 18, 24, `Bill: {invoice_number}   Date: {date}\nCashier: {staff_name}   Customer: Walk-in`, { fontSize: 7.5, color: '#374151', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d2'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 0, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d3'), y, true));
  y += 4;

  el.push(t(eid('note'), 10, y, W - 18, 22, 'All produce sourced fresh daily.\nNo returns on weighed items.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('thx'), 10, y, W - 18, 14, 'Eat Fresh. Stay Healthy! 🥦', { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

 el.push(divider(eid('d4'), y));
y += 8;

  return {
    name: 'Vegetable & Fruit Vendor — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'VEG',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 19. OPTICIAN / EYEWEAR  — Layout A (top + bottom bars, logo)
// ══════════════════════════════════════════════════════════════════════════════
export function generateOpticianThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#0f766e'; // teal
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Frame (Titanium)',     qty: 1, rate: 2499 } },
    { id: 'r2', values: { description: 'Anti-glare Lenses',    qty: 2, rate: 1200 } },
    { id: 'r3', values: { description: 'Blue Cut Coating',     qty: 2, rate: 400  } },
    { id: 'r4', values: { description: 'Lens Case + Cloth',    qty: 1, rate: 0    } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  el.push(thickBar(eid('top'), y, 5, color));
  y += 12;

  el.push(logoPlaceholder(eid('logo'), y, 32));
  y += 42;

  el.push(t(eid('name'), 6, y, W - 12, 16, 'VISTA EYEWEAR', { fontSize: 13, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('tag'), 6, y, W - 12, 12, 'Optical  •  Contact Lenses  •  Sunglasses', { fontSize: 7, textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }));
  y += 16;
  el.push(t(eid('addr'), 6, y, W - 12, 22, 'Commercial St, Bengaluru 560001\n+91 9844330011 | vistaeyewear.in', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.5 }));
  y += 26;

  el.push(divider(eid('d1'), y));
  y += 10;

  el.push(t(eid('ttl'), 6, y, W - 12, 14, 'OPTICAL PURCHASE BILL', { fontSize: 8.5, fontWeight: 'bold', textAlign: 'center' }));
  y += 18;
  el.push(t(eid('meta'), 6, y, W - 12, 26, `Bill: {invoice_number}   Date: {date}\nOptometrist: {staff_name}   Patient: [Name]`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d2'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 18, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d3'), y, true));
  y += 10;

  el.push(t(eid('wrnt'), 6, y, W - 12, 22, 'Frame warranty: 6 months.\nReady: ____________', { fontSize: 7, color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('bill'), 6, y, W - 12, 14, `Bill: {invoice_number}`, { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  el.push(thickBar(eid('bot'), y, 5, color));
  y += 10;

  return {
    name: 'Optician & Eyewear — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'OPT',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 20. COURIER / LOGISTICS  — Layout E (minimal, double rule)
// ══════════════════════════════════════════════════════════════════════════════
export function generateCourierThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#1e40af'; // navy
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Standard (2-3 Days)',  qty: 1, rate: 80  } },
    { id: 'r2', values: { description: 'Extra Weight 0.5kg',   qty: 1, rate: 25  } },
    { id: 'r3', values: { description: 'Fragile Packing',      qty: 1, rate: 40  } },
    { id: 'r4', values: { description: 'Fuel Surcharge',       qty: 1, rate: 15  } },
  ];
  let y = 8;
  const el: CanvasElement[] = [];

  el.push(logoPlaceholder(eid('logo'), y, 38));
  y += 48;

  // Double rule as accent (minimal — no color bar)
  el.push(divider(eid('r1'), y, false, color));
  y += 3;
  el.push(divider(eid('r2'), y, false, color));
  y += 10;

  el.push(t(eid('name'), 6, y, W - 12, 16, 'SWIFT COURIER SERVICES', { fontSize: 11, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('addr'), 6, y, W - 12, 22, 'Outer Ring Road, Bengaluru 560037\n+91 9900334455 | swiftcourier.in', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(divider(eid('r3'), y, false, color));
  y += 3;
  el.push(divider(eid('r4'), y, false, color));
  y += 10;
  el.push(t(eid('track'), 6, y, W - 12, 14, `AWB: {invoice_number}`, { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  el.push(t(eid('meta'), 6, y, W - 12, 36, `Date: {date}   Agent: {staff_name}\nFrom: [Sender Name]\nTo: [Receiver Name + City]`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d1'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 18, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d2'), y, true));
  y += 4;

  el.push(t(eid('note'), 6, y, W - 12, 22, 'Track at swiftcourier.in/track\nDelivery subject to address accuracy.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  // QR for tracking with bundled label
  const qrSize = 60;
  el.push({
    id: eid('qr'),
    type: 'qr',
    x: (W - qrSize) / 2,
    y,
    width: qrSize,
    height: qrSize,
    opacity: 100,
    zIndex: 10,
    rotation: 0,
    qrValue: 'https://swiftcourier.in/track',
    showLabel: true,
    labelPosition: 'bottom',
    label: 'Scan to track your shipment',
  });
  y += qrSize + 20;

  el.push(divider(eid('r5'), y, false, color));
  y += 3;
  el.push(divider(eid('r6'), y, false, color));
  y += 14;

  return {
    name: 'Courier & Logistics — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'CRR',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 21. BAKERY / SWEET SHOP  — Layout A (top + bottom bars, logo)
// ══════════════════════════════════════════════════════════════════════════════
export function generateBakeryThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#d97706'; // warm amber
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Butter Croissant x4',  qty: 1, rate: 240 } },
    { id: 'r2', values: { description: 'Red Velvet Cake 500g', qty: 1, rate: 480 } },
    { id: 'r3', values: { description: 'Kaju Katli 250g',      qty: 2, rate: 220 } },
    { id: 'r4', values: { description: 'Brown Bread Loaf',     qty: 2, rate: 65  } },
    { id: 'r5', values: { description: 'Almond Cookie Box',    qty: 1, rate: 150 } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  el.push(thickBar(eid('top'), y, 5, color));
  y += 12;

  el.push(logoPlaceholder(eid('logo'), y, 32));
  y += 42;

  el.push(t(eid('name'), 6, y, W - 12, 16, '🍰 GOLDEN OVEN BAKERY', { fontSize: 11, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('tag'), 6, y, W - 12, 12, 'Freshly Baked Every Morning', { fontSize: 7.5, textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }));
  y += 16;
  el.push(t(eid('addr'), 6, y, W - 12, 12, 'Sadashivanagar, Bengaluru | +91 9090445566', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 16;

  el.push(divider(eid('d1'), y));
  y += 10;
  el.push(t(eid('meta'), 6, y, W - 12, 24, `Bill: {invoice_number}   Date: {date}\nServer: {staff_name}`, { fontSize: 7.5, color: '#374151', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d2'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 5, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d3'), y, true));
  y += 10;

  el.push(t(eid('exp'), 6, y, W - 12, 22, 'Best before: as labeled on packaging.\nCustom cakes: 48hr advance order.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('thx'), 6, y, W - 12, 14, 'Baked with ❤️  Thank you!', { fontSize: 9, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  el.push(thickBar(eid('bot'), y, 5, color));
  y += 10;

  return {
    name: 'Bakery & Sweet Shop — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'BKR',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 22. TAILOR / BOUTIQUE  — Layout B (left sidebar)
// ══════════════════════════════════════════════════════════════════════════════
export function generateTailorThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#9333ea'; // purple
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Salwar Kameez (Stitch)',  qty: 2, rate: 550  } },
    { id: 'r2', values: { description: 'Blouse (Stitch)',         qty: 3, rate: 200  } },
    { id: 'r3', values: { description: 'Lehenga (Stitch)',        qty: 1, rate: 1200 } },
    { id: 'r4', values: { description: 'Alteration — Kurta',      qty: 2, rate: 80   } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

 

  el.push(logoPlaceholder(eid('logo'), y + 6, 30));
  y += 44;

  el.push(t(eid('name'), 10, y, W - 18, 16, 'THREAD & GRACE BOUTIQUE', { fontSize: 10, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('tag'), 10, y, W - 18, 12, 'Custom Stitching & Alterations', { fontSize: 7, textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }));
  y += 16;
  el.push(t(eid('addr'), 10, y, W - 18, 12, 'Malleswaram, Bengaluru | +91 9844556677', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 16;

  el.push(divider(eid('d1'), y));
  y += 10;
  el.push(t(eid('meta'), 10, y, W - 18, 36, `Order: {invoice_number}   Date: {date}\nMaster: {staff_name}   Customer: [Name]`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d2'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 5, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d3'), y, true));
  y += 4;

  el.push(t(eid('note'), 10, y, W - 18, 22, 'Please check stitching at delivery.\nNot responsible after 7 days.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('thx'), 10, y, W - 18, 14, 'Wear it beautifully! ✂️', { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  el.push(divider(eid('footerLine'), y, true));

  return {
    name: 'Tailor & Boutique — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'ORD',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 23. PARKING LOT / VALET  — Layout D (badge, minimal)
// ══════════════════════════════════════════════════════════════════════════════
export function generateParkingThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#374151'; // charcoal
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Car Parking (2 hrs)',   qty: 1, rate: 40 } },
    { id: 'r2', values: { description: 'Additional 1 hr',       qty: 2, rate: 20 } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  el.push(thickBar(eid('top'), y, 5, color));
  y += 12;

  el.push(logoPlaceholder(eid('logo'), y, 28));
  y += 36;

  el.push(t(eid('name'), 6, y, W - 12, 16, 'CITY SMART PARKING', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('addr'), 6, y, W - 12, 12, 'MG Road Complex, Bengaluru | +91 7788990011', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 16;

  el.push(divider(eid('d1'), y));
  y += 10;

  // Badge: PARKING TICKET
  el.push(badge(eid('bdg'), y, 140, 18, color, '#f3f4f6'));
  el.push(t(eid('bdgtxt'), (W - 140) / 2, y + 2, 140, 14, 'PARKING TICKET', { fontSize: 7.5, fontWeight: 'bold', textAlign: 'center', color }));
  y += 26;
  const inTime = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  el.push(t(eid('meta'), 6, y, W - 12, 36, `Ticket: {invoice_number}\nAttendant: {staff_name}   Vehicle: [Reg No]\nIn: {date}  ${inTime}   Out: ___`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d2'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 0, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d3'), y, true));
  y += 4;

  el.push(t(eid('note'), 6, y, W - 12, 22, 'Management not responsible for loss/damage.\nPresent ticket at exit gate.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('tok'), 6, y, W - 12, 16, `Ticket: {invoice_number}`, { fontSize: 10, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;

  el.push(thickBar(eid('bot'), y, 5, color));
  y += 10;

  return {
    name: 'Parking Lot & Valet — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'PKG',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 24. SCHOOL / COACHING  — Layout E (minimal, double rule, logo)
// ══════════════════════════════════════════════════════════════════════════════
export function generateSchoolThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#1e3a5f'; // deep navy
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Tuition Fee — June',   qty: 1, rate: 2500 } },
    { id: 'r2', values: { description: 'Lab Fee',              qty: 1, rate: 300  } },
    { id: 'r3', values: { description: 'Study Material',       qty: 1, rate: 450  } },
    { id: 'r4', values: { description: 'Late Fee (1 month)',   qty: 1, rate: 200  } },
  ];
  let y = 8;
  const el: CanvasElement[] = [];

  el.push(logoPlaceholder(eid('logo'), y, 38));
  y += 50;

  el.push(divider(eid('r1'), y, false, color));
  y += 3;
  el.push(divider(eid('r2'), y, false, color));
  y += 10;

  el.push(t(eid('name'), 6, y, W - 12, 16, 'BRIGHT FUTURE ACADEMY', { fontSize: 11, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('tag'), 6, y, W - 12, 12, 'IIT-JEE  •  NEET  •  Foundation', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 16;
  el.push(t(eid('addr'), 6, y, W - 12, 22, 'Rajajinagar, Bengaluru 560010\n+91 9845667788 | brightfuture.edu.in', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(divider(eid('r3'), y, false, color));
  y += 3;
  el.push(divider(eid('r4'), y, false, color));
  y += 10;

  el.push(t(eid('ttl'), 6, y, W - 12, 14, 'FEE RECEIPT', { fontSize: 8.5, fontWeight: 'bold', textAlign: 'center' }));
  y += 18;
  el.push(t(eid('meta'), 6, y, W - 12, 36, `Receipt: {invoice_number}   Date: {date}\nCashier: {staff_name}   Student: [Name]`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d1'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 0, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d2'), y, true));
  y += 4;

  el.push(t(eid('note'), 6, y, W - 12, 22, 'Fee once paid is non-refundable.\nNext due: ___________', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('rcpt'), 6, y, W - 12, 14, `Receipt: {invoice_number}`, { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  el.push(divider(eid('r5'), y, false, color));
  y += 3;
  el.push(divider(eid('r6'), y, false, color));
  y += 14;

  return {
    name: 'School & Coaching Centre — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'SCH',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 25. JEWELRY STORE  — Layout C (dark inverted header)
// ══════════════════════════════════════════════════════════════════════════════
export function generateJewelryThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#b45309'; // gold-brown
  const headerBg = '#1c1917';
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Gold Necklace 22K 8g',  qty: 1, rate: 42000 } },
    { id: 'r2', values: { description: 'Making Charges',        qty: 1, rate: 3500  } },
    { id: 'r3', values: { description: 'Hallmark Certificate',  qty: 1, rate: 0     } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  el.push({ id: eid('hdr'), type: 'shape', x: 0, y, width: W, height: 84, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: headerBg, borderWidth: 0, borderRadius: 0 });

  // Logo placeholder inside dark header
  el.push(logoPlaceholder(eid('logo'), y + 6, 34));

  el.push(t(eid('name'), 6, y + 8, W - 12, 16, '✦ MALABAR FINE JEWELS ✦', { fontSize: 11, fontWeight: 'bold', textAlign: 'center', color: '#fde68a' }));
  el.push(t(eid('tag'), 6, y + 26, W - 12, 12, 'Trusted since 1978', { fontSize: 7, textAlign: 'center', color: '#d6d3d1', fontStyle: 'italic' }));
  el.push(t(eid('addr'), 6, y + 40, W - 12, 22, 'Chickpete, Bengaluru 560053 | BIS Hallmarked\nGST: 29AABCU9603R1ZM | +91 9900112233', { fontSize: 7, textAlign: 'center', color: '#a8a29e', lineHeight: 1.6 }));
  y += 90;

  el.push({ id: eid('acl'), type: 'shape', x: 0, y, width: W, height: 3, opacity: 100, zIndex: 2, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 10;

  el.push(t(eid('ttl'), 6, y, W - 12, 14, 'PURCHASE RECEIPT (GOLD)', { fontSize: 8, fontWeight: 'bold', textAlign: 'center' }));
  y += 18;
  el.push(t(eid('meta'), 6, y, W - 12, 36, `Bill: {invoice_number}   Date: {date}\nSales: {staff_name}   Customer: [Name]`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d1'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 3, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d2'), y, true));
  y += 4;

  el.push(t(eid('note'), 6, y, W - 12, 22, 'Exchange at prevailing gold rate.\n100% buyback guaranteed.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('bill'), 6, y, W - 12, 14, `Bill: {invoice_number}`, { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  el.push({ id: eid('bot'), type: 'shape', x: 0, y, width: W, height: 3, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 10;

  return {
    name: 'Jewelry Store — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'JWL',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 26. SWIMMING POOL / SPORTS CLUB  — Layout A (top + bottom)
// ══════════════════════════════════════════════════════════════════════════════
export function generateSportsClubThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#0284c7'; // sky blue
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Monthly Swim Pass',   qty: 1, rate: 1200 } },
    { id: 'r2', values: { description: 'Badminton Court 1hr', qty: 2, rate: 200  } },
    { id: 'r3', values: { description: 'Swimming Coach',      qty: 4, rate: 350  } },
    { id: 'r4', values: { description: 'Locker 1 Month',      qty: 1, rate: 150  } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  el.push(thickBar(eid('top'), y, 5, color));
  y += 12;

  el.push(logoPlaceholder(eid('logo'), y, 32));
  y += 42;

  el.push(t(eid('name'), 6, y, W - 12, 16, 'AQUA ARENA SPORTS CLUB', { fontSize: 10, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('tag'), 6, y, W - 12, 12, 'Swimming  •  Badminton  •  Fitness', { fontSize: 7, textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }));
  y += 16;
  el.push(t(eid('addr'), 6, y, W - 12, 12, 'Whitefield, Bengaluru | +91 9901122334', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 16;

  el.push(divider(eid('d1'), y));
  y += 10;
  el.push(t(eid('meta'), 6, y, W - 12, 36, `Receipt: {invoice_number}   Date: {date}\nStaff: {staff_name}   Member: [Name]`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d2'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 18, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d3'), y, true));
  y += 4;

  el.push(t(eid('note'), 6, y, W - 12, 22, 'Pass valid for 30 days.\nNon-transferable. No refunds.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('thx'), 6, y, W - 12, 14, 'Stay active. Stay healthy! 🏊', { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  el.push(thickBar(eid('bot'), y, 5, color));
  y += 10;

  return {
    name: 'Swimming Pool & Sports Club — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'SPT',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 27. VETERINARY CLINIC  — Layout D (badge)
// ══════════════════════════════════════════════════════════════════════════════
export function generateVetThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#065f46'; // deep green
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Consultation Fee',      qty: 1, rate: 400 } },
    { id: 'r2', values: { description: 'Vaccination (Rabies)',  qty: 1, rate: 350 } },
    { id: 'r3', values: { description: 'Deworming Tablet',      qty: 2, rate: 60  } },
    { id: 'r4', values: { description: 'Flea Treatment',        qty: 1, rate: 250 } },
    { id: 'r5', values: { description: 'CBC Blood Test',        qty: 1, rate: 700 } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  el.push(thickBar(eid('top'), y, 5, color));
  y += 12;

  el.push(logoPlaceholder(eid('logo'), y, 30));
  y += 38;

  el.push(t(eid('name'), 6, y, W - 12, 16, '🐾 PAWS & CARE VET CLINIC', { fontSize: 10, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('addr'), 6, y, W - 12, 22, 'Koramangala, Bengaluru 560034\nDr. Kavita Rao  BVSc, MVSc | +91 9900223344', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(divider(eid('d1'), y));
  y += 10;

  // Badge
  el.push(badge(eid('bdg'), y, 130, 18, color, '#ecfdf5'));
  el.push(t(eid('bdgtxt'), (W - 130) / 2, y + 2, 130, 14, 'PATIENT RECEIPT', { fontSize: 7.5, fontWeight: 'bold', textAlign: 'center', color }));
  y += 26;
  el.push(t(eid('meta'), 6, y, W - 12, 36, `Receipt: {invoice_number}   Date: {date}\nDoctor: {staff_name}   Pet: [Name]`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d2'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 0, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d3'), y, true));
  y += 4;

  el.push(t(eid('note'), 6, y, W - 12, 22, 'Next vaccine due: ___________\nEmergency: +91 9900224455 (24/7)', { fontSize: 7, color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('thx'), 6, y, W - 12, 14, 'Get well soon, little one! 🐶', { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  el.push(thickBar(eid('bot'), y, 5, color));
  y += 10;

  return {
    name: 'Veterinary Clinic — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'VET',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 28. FLORIST / GIFT SHOP  — Layout E (minimal, double rule)
// ══════════════════════════════════════════════════════════════════════════════
export function generateFloristThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#be185d'; // rose pink
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Red Rose Bouquet 12pc', qty: 1, rate: 350 } },
    { id: 'r2', values: { description: 'Lily Arrangement',       qty: 1, rate: 280 } },
    { id: 'r3', values: { description: 'Birthday Balloon Set',   qty: 1, rate: 120 } },
    { id: 'r4', values: { description: 'Gift Wrap + Ribbon',     qty: 1, rate: 80  } },
    { id: 'r5', values: { description: 'Message Card',           qty: 1, rate: 0   } },
  ];
  let y = 8;
  const el: CanvasElement[] = [];

  el.push(logoPlaceholder(eid('logo'), y, 36));
  y += 46;

  el.push(divider(eid('r1'), y, false, color));
  y += 3;
  el.push(divider(eid('r2'), y, false, color));
  y += 10;

  el.push(t(eid('name'), 6, y, W - 12, 16, '🌸 BLOOMS & BLISS', { fontSize: 12, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('tag'), 6, y, W - 12, 12, 'Fresh Flowers & Handcrafted Gifts', { fontSize: 7.5, textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }));
  y += 16;
  el.push(t(eid('addr'), 6, y, W - 12, 12, 'Frazer Town, Bengaluru | +91 9844112233', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 16;

  el.push(divider(eid('r3'), y, false, color));
  y += 3;
  el.push(divider(eid('r4'), y, false, color));
  y += 10;
  el.push(t(eid('meta'), 6, y, W - 12, 24, `Bill: {invoice_number}   Date: {date}\nStaff: {staff_name}   Occasion: [Occasion]`, { fontSize: 7.5, color: '#374151', lineHeight: 1.6 }));
  y += 28;

  el.push(divider(eid('d1'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 5, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d2'), y, true));
  y += 4;

  el.push(t(eid('note'), 6, y, W - 12, 22, 'Flowers best kept in water.\nCustom orders: 24hr advance.', { fontSize: 7, textAlign: 'center', color: '#6b7280', lineHeight: 1.6 }));
  y += 26;

  el.push(t(eid('thx'), 6, y, W - 12, 14, 'Spread joy with flowers! 💐', { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  el.push(divider(eid('r5'), y, false, color));
  y += 3;
  el.push(divider(eid('r6'), y, false, color));
  y += 14;

  return {
    name: 'Florist & Gift Shop — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'FLR',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 29. PRINTING / XEROX SHOP  — Layout B (left sidebar)
// ══════════════════════════════════════════════════════════════════════════════
export function generatePrintShopThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#1e3a5f'; // deep navy
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'A4 Xerox (B&W) x50',   qty: 50,  rate: 1  } },
    { id: 'r2', values: { description: 'Color Print A4 x10',   qty: 10,  rate: 8  } },
    { id: 'r3', values: { description: 'Spiral Binding',       qty: 2,   rate: 50 } },
    { id: 'r4', values: { description: 'Lamination A4',        qty: 4,   rate: 20 } },
    { id: 'r5', values: { description: 'Visiting Cards x100',  qty: 1,   rate: 350} },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  el.push(logoPlaceholder(eid('logo'), y + 6, 28));
  y += 42;

  el.push(t(eid('name'), 10, y, W - 18, 16, 'PRINTMASTER STUDIO', { fontSize: 11, fontWeight: 'bold', textAlign: 'center', color }));
  y += 20;
  el.push(t(eid('tag'), 10, y, W - 18, 12, 'Printing  •  Binding  •  Design', { fontSize: 7, textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }));
  y += 16;
  el.push(t(eid('addr'), 10, y, W - 18, 12, 'Near Bus Stand, Mysuru | +91 9845339900', { fontSize: 7, textAlign: 'center', color: '#6b7280' }));
  y += 16;

  el.push(divider(eid('d1'), y));
  y += 10;
  el.push(t(eid('meta'), 10, y, W - 18, 24, `Bill: {invoice_number}   Date: {date}\nStaff: {staff_name}`, { fontSize: 7.5, color: '#374151', lineHeight: 1.6 }));  y += 28;

  el.push(divider(eid('d2'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 18, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  el.push(divider(eid('d3'), y, true));
  y += 10;

  el.push(t(eid('thx'), 10, y, W - 18, 14, 'Thank you! Print smart.', { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  el.push(divider(eid('d4'), y));

  return {
    name: 'Printing & Xerox Shop — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'PRT',
  } as any;
}


// ══════════════════════════════════════════════════════════════════════════════
// 30. EVENT TICKET / CINEMA  — Layout C (dark inverted header + QR)
// ══════════════════════════════════════════════════════════════════════════════
export function generateEventTicketThermal(): ActiveTemplate {
  _eid = 0;
  const color = '#7c3aed'; // violet
  const headerBg = '#0f0f1a'; // near-black purple
  const rows: TableRow[] = [
    { id: 'r1', values: { description: 'Gold Seat A12',        qty: 2, rate: 350 } },
    { id: 'r2', values: { description: 'Popcorn Combo Lrg',    qty: 2, rate: 180 } },
    { id: 'r3', values: { description: 'Convenience Fee',      qty: 1, rate: 30  } },
  ];
  let y = 0;
  const el: CanvasElement[] = [];

  // Dark cinema header
  el.push({ id: eid('hdr'), type: 'shape', x: 0, y, width: W, height: 80, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: headerBg, borderWidth: 0, borderRadius: 0 });

  el.push(logoPlaceholder(eid('logo'), y + 6, 32));

  el.push(t(eid('name'), 6, y + 8, W - 12, 16, '★ CINEMAX MULTIPLEX ★', { fontSize: 11, fontWeight: 'bold', textAlign: 'center', color: '#ddd6fe' }));
  el.push(t(eid('tag'), 6, y + 26, W - 12, 12, '5-screen Dolby Atmos Experience', { fontSize: 7, textAlign: 'center', color: '#a78bfa', fontStyle: 'italic' }));
  el.push(t(eid('addr'), 6, y + 40, W - 12, 22, 'Forum Mall, Bengaluru | +91 9900555666\ncinemax.in', { fontSize: 7, textAlign: 'center', color: '#6d28d9', lineHeight: 1.6 }));
  y += 86;

  el.push({ id: eid('acl'), type: 'shape', x: 0, y, width: W, height: 3, opacity: 100, zIndex: 2, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 10;

  el.push(t(eid('mov'), 6, y, W - 12, 18, 'MOVIE: [FILM NAME]', { fontSize: 10, fontWeight: 'bold', textAlign: 'center', color }));
  y += 22;
  const timeStr = '7:30 PM';
  el.push(t(eid('meta'), 6, y, W - 12, 36, `Booking: {invoice_number}   Date: {date}\nOperator: {staff_name}   Show: ${timeStr}\nScreen: 3  Format: 2D`, { fontSize: 7.5, color: '#374151', lineHeight: 1.7 }));
  y += 42;

  el.push(divider(eid('d1'), y));
  y += 8;

  const tbl = thermalTable(eid('tbl'), y, color, rows, 0, rows.length);
  el.push(tbl);
  y += (tbl.height as number) + 4;

  // Dashed tear line
  el.push(divider(eid('tear'), y, true, '#6b7280'));
  y += 10;

  // QR code for ticket with bundled label
  const qrSize = 64;
  el.push({
    id: eid('qr'),
    type: 'qr',
    x: (W - qrSize) / 2,
    y,
    width: qrSize,
    height: qrSize,
    opacity: 100,
    zIndex: 10,
    rotation: 0,
    qrValue: 'https://cinemax.in/ticket',
    showLabel: true,
    labelPosition: 'bottom',
    label: 'Scan at entry gate',
  });
  y += qrSize + 20;

  el.push(t(eid('bkid'), 6, y, W - 12, 14, `Booking ID: {invoice_number}`, { fontSize: 8, fontWeight: 'bold', textAlign: 'center', color }));
  y += 18;

  el.push({ id: eid('bot'), type: 'shape', x: 0, y, width: W, height: 3, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  y += 10;

  return {
    name: 'Event Ticket & Cinema — Thermal Receipt',
    pageSize: 'THERMAL_80MM',
    canvasHeight: y,
    width: W,
    height: y,
    elements: el,
    category: 'POS Receipts',
    receiptType: 'EVT',
  } as any;
}
// ══════════════════════════════════════════════════════════════════════════════
// EXPORTS
// ══════════════════════════════════════════════════════════════════════════════
export const THERMAL_PRESETS_GENERATORS = [
  generateGroceryThermal,
  generateClinicThermal,
  generateRestaurantThermal,
  generateRetailThermal,
  generateCafeThermal,
  generatePharmacyThermal,
  generateFastFoodThermal,
  generateSalonThermal,
  generateFuelStationThermal,
  generateGymThermal,
  generateHotelThermal,
  generateElectronicsThermal,
  generateHardwareThermal,
  generateRechargeThermal,
  generateStationeryThermal,
  generateLaundryThermal,
  generateGarageThermal,
  generateVegetableThermal,
  generateOpticianThermal,
  generateCourierThermal,
  generateBakeryThermal,
  generateTailorThermal,
  generateParkingThermal,
  generateSchoolThermal,
  generateJewelryThermal,
  generateSportsClubThermal,
  generateVetThermal,
  generateFloristThermal,
  generatePrintShopThermal,
  generateEventTicketThermal,
];

export const THERMAL_PRESETS: ActiveTemplate[] = THERMAL_PRESETS_GENERATORS
  .map(fn => ({ ...fn(), category: 'POS Receipts' })) as ActiveTemplate[];